var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as qs from 'qs';
import { SessionError, SessionErrorKey } from '../error.mjs';
import { combineErrorMappers, getInvalidFieldErrorMapper, getSimpleErrorMapper, mapKeycloakError, } from './errorMappers.mjs';
import { HeadersBuilder } from './headersBuilder.mjs';
import { KeycloakHttpAPI } from './httpAPI.mjs';
import { KeycloakAPIErrorCode, KeycloakAPIGrantType, KeycloakAuthMethod, KeycloakEmailChallengeType, KeycloakEndpoint, KeycloakMedium, KeycloakOtpAction, OAuthTokenTypeIdentifier, } from './types.mjs';
/**
 * Client for our Keycloak Server.
 *
 * Makes use of the Keycloak API and the changes added in:
 * https://github.com/SectorLabs/keycloak-authenticators
 */
export class KeycloakClient {
    constructor(config) {
        this.config = config;
        this.invalidEmailErrorMappers = {
            [KeycloakAPIErrorCode.CLIENT_INVALID]: getInvalidFieldErrorMapper({
                newErrorName: SessionErrorKey.VALIDATION_ERROR,
                field: 'client_id',
            }),
            [KeycloakAPIErrorCode.EMAIL_INVALID]: getInvalidFieldErrorMapper({
                newErrorName: SessionErrorKey.VALIDATION_ERROR,
                field: 'email',
            }),
            [KeycloakAPIErrorCode.INVALID_REQUEST]: getInvalidFieldErrorMapper({
                oldErrorName: SessionErrorKey.ACCESS_DENIED,
                newErrorName: SessionErrorKey.VALIDATION_ERROR,
                field: 'email',
            }),
            [KeycloakAPIErrorCode.CHALLENGE_TYPE_INVALID]: getInvalidFieldErrorMapper({
                newErrorName: SessionErrorKey.VALIDATION_ERROR,
                field: 'type',
            }),
            [KeycloakAPIErrorCode.USER_DISABLED]: getInvalidFieldErrorMapper({
                oldErrorName: SessionErrorKey.ACCESS_DENIED,
                newErrorName: SessionErrorKey.USER_DISABLED,
                field: 'email',
            }),
            [KeycloakAPIErrorCode.CODE_SEND_FAILED]: getInvalidFieldErrorMapper({
                oldErrorName: SessionErrorKey.SERVER,
                newErrorName: SessionErrorKey.CODE_SEND_FAILED,
                field: 'email',
            }),
        };
        this.invalidPhoneErrorMappers = {
            [KeycloakAPIErrorCode.CLIENT_INVALID]: getInvalidFieldErrorMapper({
                newErrorName: SessionErrorKey.VALIDATION_ERROR,
                field: 'client_id',
            }),
            [KeycloakAPIErrorCode.INVALID_REQUEST]: combineErrorMappers([
                getInvalidFieldErrorMapper({
                    newErrorName: SessionErrorKey.VALIDATION_ERROR,
                    field: 'phoneNumber',
                }),
                getInvalidFieldErrorMapper({
                    oldErrorName: SessionErrorKey.ACCESS_DENIED,
                    newErrorName: SessionErrorKey.VALIDATION_ERROR,
                    field: 'phoneNumber',
                }),
            ]),
            [KeycloakAPIErrorCode.USER_DISABLED]: getInvalidFieldErrorMapper({
                oldErrorName: SessionErrorKey.ACCESS_DENIED,
                newErrorName: SessionErrorKey.USER_DISABLED,
                field: 'phoneNumber',
            }),
            [KeycloakAPIErrorCode.CODE_SEND_FAILED]: getInvalidFieldErrorMapper({
                oldErrorName: SessionErrorKey.SERVER,
                newErrorName: SessionErrorKey.CODE_SEND_FAILED,
                field: 'phoneNumber',
            }),
        };
        this.otpErrorMappers = {
            [KeycloakAPIErrorCode.CODE_TOKEN_INVALID]: getSimpleErrorMapper({
                oldErrorName: SessionErrorKey.ACCESS_DENIED,
                newErrorName: SessionErrorKey.INVALID_OTP_TOKEN,
            }),
            [KeycloakAPIErrorCode.CODE_EXPIRED]: getSimpleErrorMapper({
                oldErrorName: SessionErrorKey.GONE,
                newErrorName: SessionErrorKey.OTP_CODE_EXPIRED,
            }),
        };
        this.tokenExchangeErrorMappers = {
            [KeycloakAPIErrorCode.USER_DISABLED]: getSimpleErrorMapper({
                oldErrorName: SessionErrorKey.ACCESS_DENIED,
                newErrorName: SessionErrorKey.USER_DISABLED,
            }),
        };
        this.authorizationCodeErrorMappers = {
            [KeycloakAPIErrorCode.USER_DISABLED]: getSimpleErrorMapper({
                oldErrorName: SessionErrorKey.ACCESS_DENIED,
                newErrorName: SessionErrorKey.USER_DISABLED,
            }),
        };
        this.emailAuthMethodErrorMappers = {
            [KeycloakAPIErrorCode.CLIENT_INVALID]: getInvalidFieldErrorMapper({
                newErrorName: SessionErrorKey.VALIDATION_ERROR,
                field: 'client_id',
            }),
            [KeycloakAPIErrorCode.EMAIL_INVALID]: getInvalidFieldErrorMapper({
                newErrorName: SessionErrorKey.VALIDATION_ERROR,
                field: 'email',
            }),
            [KeycloakAPIErrorCode.INVALID_REQUEST]: getInvalidFieldErrorMapper({
                oldErrorName: SessionErrorKey.ACCESS_DENIED,
                newErrorName: SessionErrorKey.VALIDATION_ERROR,
                field: 'email',
            }),
            [KeycloakAPIErrorCode.USER_DISABLED]: getInvalidFieldErrorMapper({
                oldErrorName: SessionErrorKey.ACCESS_DENIED,
                newErrorName: SessionErrorKey.USER_DISABLED,
                field: 'email',
            }),
        };
        this.phoneAuthMethodErrorMappers = {
            [KeycloakAPIErrorCode.CLIENT_INVALID]: getInvalidFieldErrorMapper({
                newErrorName: SessionErrorKey.VALIDATION_ERROR,
                field: 'client_id',
            }),
            [KeycloakAPIErrorCode.INVALID_REQUEST]: combineErrorMappers([
                getInvalidFieldErrorMapper({
                    newErrorName: SessionErrorKey.VALIDATION_ERROR,
                    field: 'phoneNumber',
                }),
                getInvalidFieldErrorMapper({
                    oldErrorName: SessionErrorKey.ACCESS_DENIED,
                    newErrorName: SessionErrorKey.VALIDATION_ERROR,
                    field: 'phoneNumber',
                }),
            ]),
            [KeycloakAPIErrorCode.USER_DISABLED]: getInvalidFieldErrorMapper({
                oldErrorName: SessionErrorKey.ACCESS_DENIED,
                newErrorName: SessionErrorKey.USER_DISABLED,
                field: 'phoneNumber',
            }),
        };
        this.httpAPI = new KeycloakHttpAPI(this.openIDURL, {
            timeout: config.timeout,
            iso639S1LanguageCode: config.iso639S1LanguageCode,
            defaultRequestCredentials: config.defaultRequestCredentials || 'same-origin',
            defaultRequestHeaders: config.defaultRequestHeaders || {},
        });
        this.clientID = config.clientID;
        this.iso639S1LanguageCode = config.iso639S1LanguageCode;
    }
    resolveEmailAuthMethod(email, captcha) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const data = yield this.sendRequest(KeycloakEndpoint.TOKEN, this.createGrantRequestBody(KeycloakAPIGrantType.PASSWORD, Object.assign({ type: KeycloakMedium.EMAIL, email }, (captcha && { captcha }))));
                return {
                    requiredType: data.required_type,
                    isNewlyRegistered: data.is_newly_registered,
                };
            }
            catch (error) {
                throw mapKeycloakError(error, this.emailAuthMethodErrorMappers);
            }
        });
    }
    resolvePhoneAuthMethod(phoneNumber, captcha) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const data = yield this.sendRequest(KeycloakEndpoint.TOKEN, this.createGrantRequestBody(KeycloakAPIGrantType.PASSWORD, Object.assign({ type: KeycloakMedium.PHONE, phone_number: phoneNumber }, (captcha && { captcha }))));
                return {
                    requiredType: data.required_type,
                    isNewlyRegistered: data.is_newly_registered,
                };
            }
            catch (error) {
                throw mapKeycloakError(error, this.phoneAuthMethodErrorMappers);
            }
        });
    }
    sendEmailLink(_a) {
        return __awaiter(this, arguments, void 0, function* ({ email, redirectURI, captcha, initialAttributes, }) {
            try {
                yield this.sendJSONRequest(KeycloakEndpoint.PASSWORDLESS_LOGIN_GENERATE_EMAIL_CHALLENGE, Object.assign({ client_id: this.clientID, email, type: KeycloakEmailChallengeType.LINK, redirect_uri: redirectURI, initial_attributes: initialAttributes }, (captcha && { captcha })));
            }
            catch (error) {
                throw mapKeycloakError(error, this.invalidEmailErrorMappers);
            }
        });
    }
    sendEmailCode(_a) {
        return __awaiter(this, arguments, void 0, function* ({ email, captcha, initialAttributes, }) {
            try {
                const data = yield this.sendJSONRequest(KeycloakEndpoint.PASSWORDLESS_LOGIN_GENERATE_EMAIL_CHALLENGE, Object.assign({ client_id: this.clientID, type: KeycloakEmailChallengeType.CODE, email, initial_attributes: initialAttributes }, (captcha && { captcha })));
                return { token: data.token, expiresIn: data.expires_in };
            }
            catch (error) {
                throw mapKeycloakError(error, this.invalidEmailErrorMappers);
            }
        });
    }
    resendEmailCode(email, token, captcha) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield this.sendJSONRequest(KeycloakEndpoint.PASSWORDLESS_LOGIN_RESEND_EMAIL_CHALLENGE, Object.assign({ client_id: this.clientID, email,
                    token }, (captcha && { captcha })));
            }
            catch (error) {
                throw mapKeycloakError(error, Object.assign(Object.assign({}, this.invalidEmailErrorMappers), this.otpErrorMappers));
            }
        });
    }
    sendPhoneCode(_a) {
        return __awaiter(this, arguments, void 0, function* ({ phone, channel, captcha, initialAttributes, }) {
            try {
                const data = yield this.sendJSONRequest(KeycloakEndpoint.PASSWORDLESS_LOGIN_GENERATE_PHONE_NUMBER_CHALLENGE, Object.assign({ client_id: this.clientID, medium: channel, phone_number: phone, initial_attributes: initialAttributes }, (captcha && { captcha })));
                return { token: data.token, expiresIn: data.expires_in };
            }
            catch (error) {
                throw mapKeycloakError(error, this.invalidPhoneErrorMappers);
            }
        });
    }
    resendPhoneCode(phoneNumber, sendMethod, token, captcha) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield this.sendJSONRequest(KeycloakEndpoint.PASSWORDLESS_LOGIN_RESEND_PHONE_NUMBER_CHALLENGE, Object.assign({ client_id: this.clientID, medium: sendMethod, phone_number: phoneNumber, token }, (captcha && { captcha })));
            }
            catch (error) {
                throw mapKeycloakError(error, Object.assign(Object.assign({}, this.invalidPhoneErrorMappers), this.otpErrorMappers));
            }
        });
    }
    emailPasswordGrant(email, password) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.grant(KeycloakAPIGrantType.PASSWORD, {
                type: KeycloakAuthMethod.EMAIL_PASSWORD,
                email,
                password,
            }, this.invalidEmailErrorMappers);
        });
    }
    emailCodeGrant(email, token, code, password) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.grant(KeycloakAPIGrantType.PASSWORD, {
                type: KeycloakAuthMethod.EMAIL_CODE,
                action: KeycloakOtpAction.VERIFY,
                email,
                token,
                code,
                password,
            }, Object.assign(Object.assign({}, this.invalidEmailErrorMappers), this.otpErrorMappers));
        });
    }
    phonePasswordGrant(phoneNumber, password) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.grant(KeycloakAPIGrantType.PASSWORD, {
                type: KeycloakAuthMethod.PHONE_PASSWORD,
                phone_number: phoneNumber,
                password,
            }, this.invalidPhoneErrorMappers);
        });
    }
    phoneCodeGrant(phoneNumber, token, code, password) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.grant(KeycloakAPIGrantType.PASSWORD, {
                type: KeycloakAuthMethod.PHONE_CODE,
                action: KeycloakOtpAction.VERIFY,
                phone_number: phoneNumber,
                token,
                code,
                password,
            }, Object.assign(Object.assign({}, this.invalidPhoneErrorMappers), this.otpErrorMappers));
        });
    }
    refreshGrant(refreshToken) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.grant(KeycloakAPIGrantType.REFRESH_TOKEN, {
                refresh_token: refreshToken,
            });
        });
    }
    authorizationCodeGrant(authorizationCode, redirectURI) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.grant(KeycloakAPIGrantType.AUTHORIZATION_CODE, Object.assign({ code: authorizationCode }, (redirectURI && { redirect_uri: redirectURI })), this.authorizationCodeErrorMappers);
        });
    }
    tokenExchangeGrant(provider, token, subjectTokenType, redirectURL) {
        return __awaiter(this, void 0, void 0, function* () {
            const grantParams = {
                subject_token_type: subjectTokenType,
                requested_token_type: OAuthTokenTypeIdentifier.REFRESH_TOKEN,
                subject_issuer: provider,
                subject_token: token,
            };
            if (redirectURL) {
                grantParams.redirect_uri = redirectURL;
            }
            return this.grant(KeycloakAPIGrantType.TOKEN_EXCHANGE, grantParams, this.tokenExchangeErrorMappers);
        });
    }
    revokeGrant(refreshToken) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.sendRequest(KeycloakEndpoint.LOGOUT, {
                client_id: this.clientID,
                refresh_token: refreshToken,
            });
        });
    }
    reserveUserByEmail(email, captcha) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const data = yield this.sendJSONRequest(KeycloakEndpoint.EMAIL_RESERVE_USER, Object.assign({ client_id: this.clientID, scope: this.scopes.join(' '), email }, (captcha && { captcha })));
                if (data.is_newly_registered) {
                    return {
                        id: data.id,
                        externalID: data.external_id,
                        isNewlyRegistered: true,
                        authorizationCode: data.authorization_code,
                    };
                }
                return {
                    id: data.id,
                    externalID: data.external_id,
                    isNewlyRegistered: false,
                    authorizationCode: null,
                };
            }
            catch (error) {
                if (error instanceof SessionError && (error === null || error === void 0 ? void 0 : error.name) === SessionErrorKey.CONFLICT) {
                    const data = error.extra.httpResponseBody;
                    return {
                        id: data.id,
                        externalID: data.external_id,
                        isNewlyRegistered: data.is_newly_registered,
                        // Keycloak isn't supposed to provide a authorization code to login
                        // with for non-new users. That would be a major security breach.
                        // Forcing this to null here is a last-ditch attempt at limiting
                        // the consequences if Keycloak were to return authorization codes
                        // for existing users.
                        authorizationCode: null,
                    };
                }
                throw mapKeycloakError(error, this.invalidEmailErrorMappers);
            }
        });
    }
    createLoginURL({ redirectURI, idpHint, additionalQueryParameters, }) {
        const queryParams = {
            client_id: this.clientID,
            // TODO: make these configurable
            response_mode: 'fragment',
            response_type: 'code',
            login: true,
            redirect_uri: redirectURI,
            // If this is set, Keycloak will immediately
            // start this login method. For example, if set
            // to 'google', it will immediately redirect to
            // the Google login page.
            kc_idp_hint: idpHint,
            ui_locales: this.iso639S1LanguageCode,
            scope: this.scopes.join(' '),
        };
        if (additionalQueryParameters) {
            Object.assign(queryParams, additionalQueryParameters);
        }
        const serializedQueryParams = qs.stringify(queryParams);
        return `${this.openIDURL}${KeycloakEndpoint.AUTH}?${serializedQueryParams}`;
    }
    grant(grantType_1, grantParameters_1) {
        return __awaiter(this, arguments, void 0, function* (grantType, grantParameters, errorMappers = {}) {
            let data = null;
            try {
                data = yield this.sendRequest(KeycloakEndpoint.TOKEN, this.createGrantRequestBody(grantType, grantParameters));
            }
            catch (error) {
                throw mapKeycloakError(error, errorMappers);
            }
            if (!data.access_token || !data.refresh_token || !data.id_token) {
                throw new SessionError('Missing tokens in response.', {
                    name: SessionErrorKey.MALFORMED_RESPONSE,
                    // Do not add response data here. Might contain sensitive data.
                });
            }
            const keycloakTokenData = {
                accessToken: data.access_token,
                expiresIn: data.expires_in,
                notBeforePolicy: data['not-before-policy'],
                refreshToken: data.refresh_token,
                refreshExpiresIn: data.refresh_expires_in,
                idToken: data.id_token,
                tokenType: data.token_type,
                scopes: (data.scope || '').split(' '),
                sessionState: data.session_state,
            };
            //add optional fields only if present
            if (data.is_newly_registered) {
                keycloakTokenData.isNewlyRegistered = data.is_newly_registered;
            }
            return keycloakTokenData;
        });
    }
    createGrantRequestBody(grantType, grantParameters) {
        return Object.assign({ grant_type: grantType, client_id: this.clientID, scope: this.scopes.join(' ') }, grantParameters);
    }
    get openIDURL() {
        return this.createURL(`/auth/realms/${this.config.realm}/`);
    }
    createURL(path) {
        // Strip trailing slash if needed.. Poor man's URL join
        // We can't use the URL class here because it is not
        // available in all browsers and we don't wanna pull
        // in a package just for this one use case.
        const baseURL = this.config.url.endsWith('/')
            ? this.config.url.slice(0, -1)
            : this.config.url;
        return baseURL + path;
    }
    get scopes() {
        return Array.from(new Set([...(this.config.scopes || []), ...KeycloakClient.defaultScopes]));
    }
    sendRequest(endpoint, body) {
        const headers = new HeadersBuilder().addUrlEncodedContentTypeHeader().build();
        return this.httpAPI.post(endpoint, qs.stringify(body), headers);
    }
    sendJSONRequest(endpoint, body) {
        const headers = new HeadersBuilder().addJsonContentTypeHeader().build();
        return this.httpAPI.post(endpoint, JSON.stringify(body), headers);
    }
}
KeycloakClient.defaultScopes = ['openid'];
